import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Check, ArrowRight, Ruler } from 'lucide-react';

export default function GutterMeasurement() {
  const navigate = useNavigate();
  const { isAuthenticated, openAuthModal } = useAuth();
  const handleStart = () => (isAuthenticated ? navigate('/dashboard') : openAuthModal('signup'));

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Gutter Measurement Software | Estimate Gutters from Satellite | SquareCount</title>
        <meta name="description" content="Measure gutters from satellite imagery in seconds. SquareCount estimates linear feet of gutters, downspouts, elbows, and end caps alongside your roof measurement — included with every paid plan. Free 7-day Contractor trial." />
        <meta property="og:title" content="Gutter Measurement Software | SquareCount" />
        <meta property="og:description" content="Estimate gutter linear feet, downspouts, and materials from satellite imagery in seconds — included with every paid plan." />
        <link rel="canonical" href="https://squarecount.io/gutter-measurement" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'SquareCount Gutter Measurement',
          description: 'Measure gutters from satellite imagery — linear feet, downspouts, elbows, end caps, and material estimates — included with every paid SquareCount plan.',
          brand: { '@type': 'Brand', name: 'SquareCount' },
          image: 'https://squarecount.io/logo.png',
          url: 'https://squarecount.io/gutter-measurement',
          offers: { '@type': 'Offer', price: '19.00', priceCurrency: 'USD', availability: 'https://schema.org/InStock', url: 'https://squarecount.io/pricing' },
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            { '@type': 'Question', name: 'Can you measure gutters from satellite imagery?', acceptedAnswer: { '@type': 'Answer', text: 'Yes. SquareCount estimates gutter linear feet by tracing the roof eaves on satellite imagery, then calculates downspouts, elbows, end caps, hangers, and sealant so you can quote a gutter job in about a minute — no ladder or site visit required.' } },
            { '@type': 'Question', name: 'How do I estimate linear feet of gutters?', acceptedAnswer: { '@type': 'Answer', text: 'Gutter linear footage is the total length of the roof eaves (the lower horizontal edges). SquareCount measures the eave length from satellite imagery and converts it to gutter sections, downspouts, and material counts automatically.' } },
            { '@type': 'Question', name: 'How much does gutter measurement cost with SquareCount?', acceptedAnswer: { '@type': 'Answer', text: 'Gutter estimates are included with every paid SquareCount plan (from $19/month), and every new account starts with a free 7-day Contractor trial with every feature unlocked. There is no per-report fee.' } },
          ],
        })}</script>
      </Helmet>
      <Navbar />

      <section className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-20 lg:py-24">
          <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
            <Ruler className="w-4 h-4 text-orange-400" />
            <span className="text-orange-300 text-sm font-medium">Gutter estimates included with every paid plan</span>
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight mb-6">
            Measure Gutters from Satellite — in Seconds
          </h1>
          <p className="text-xl text-slate-300 mb-8 max-w-3xl">
            SquareCount estimates gutter linear feet, downspouts, elbows, and end caps straight from satellite
            imagery — alongside your roof measurement. Quote a gutter job from your desk in about a minute,
            with flat monthly pricing (3 free reports to start). No ladder, no site visit.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button onClick={handleStart} className="bg-orange-500 hover:bg-orange-400 text-white h-14 px-8 font-heading font-bold tracking-wide text-lg">
              Start Your 7-Day Contractor Trial <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button onClick={() => navigate('/pricing')} className="h-14 px-8 font-heading font-bold tracking-wide text-lg border-2 border-white/20 text-white hover:bg-white/10 bg-transparent">
              See Pricing
            </Button>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="font-heading font-black text-3xl text-slate-900 mb-8">What you get with every gutter estimate</h2>
        <ul className="grid sm:grid-cols-2 gap-4">
          {[
            'Total gutter linear feet (from the roof eaves)',
            'Number of 10-ft gutter sections',
            'Downspouts, elbows, and end caps',
            'Hangers, screws, and sealant counts',
            'Inside and outside corner counts',
            'Roof + gutter measured together in one pass',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3 bg-slate-50 rounded-xl p-5 border border-slate-200">
              <Check className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
              <span className="text-slate-700">{item}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <h2 className="font-heading font-black text-3xl text-slate-900 mb-8">How to measure gutters from satellite</h2>
          <ol className="space-y-4 text-slate-700 list-decimal list-inside">
            <li>Enter the property address — high-resolution satellite imagery loads instantly.</li>
            <li>Trace the roof outline; SquareCount identifies the eave edges where gutters run.</li>
            <li>Get gutter linear feet plus a full material list — downspouts, elbows, hangers, and more.</li>
            <li>Export a professional PDF estimate or use the numbers in your own quote.</li>
          </ol>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-16">
        <h2 className="font-heading font-black text-3xl text-slate-900 mb-8">Gutter measurement — frequently asked questions</h2>
        <div className="space-y-6">
          {[
            ['Can you measure gutters from satellite imagery?', 'Yes. SquareCount estimates gutter linear feet by tracing the roof eaves on satellite imagery, then calculates downspouts, elbows, end caps, hangers, and sealant — so you can quote a gutter job in about a minute, no ladder required.'],
            ['How do I estimate linear feet of gutters?', 'Gutter linear footage is the total length of the roof eaves (the lower horizontal edges). SquareCount measures the eave length from satellite imagery and converts it to gutter sections, downspouts, and material counts automatically.'],
            ['How much does it cost?', 'Gutter estimates are included with every paid SquareCount plan (from $19/month), and every new account starts with a free 7-day Contractor trial with every feature unlocked. No per-report fees.'],
          ].map(([q, a], i) => (
            <div key={i} className="border-b border-slate-200 pb-6">
              <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">{q}</h3>
              <p className="text-slate-600">{a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <h2 className="font-heading font-black text-3xl mb-4">Quote roofs and gutters in one pass</h2>
          <p className="text-slate-300 mb-8">Free to start — 3 reports per month. Try every Contractor feature free for 7 days. No contract.</p>
          <Button onClick={handleStart} className="bg-orange-500 hover:bg-orange-400 text-white h-14 px-10 font-heading font-bold tracking-wide text-lg">
            Start Your 7-Day Contractor Trial <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
